import { Component, Input, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { VehicleCurrentInfo } from 'src/app/_shared/models/vehicle';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { RangeYPipe } from 'src/app/_shared/pipes/range-y.pipe';

@Component({
  selector: 'app-ev-condition-plot',
  templateUrl: './ev-condition-plot.component.html',
  styleUrls: ['./ev-condition-plot.component.scss']
})
export class EvConditionPlotComponent {
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() vehiclesInfo: { [key: string]: VehicleCurrentInfo; } = {};
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  @Input() dataLoadingError: boolean = false;
  private colorMap: { [key: string]: string } = {};
  private readonly kmToMilesPipe = new KmToMilesPipe();
  yValues: number[] = [];
  private readonly rangeYPipe = new RangeYPipe();

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetGeneralFigureService.getReferenceFleet().pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          const plotData = datapoints.sort((a: any, b: any) => a.distance - b.distance);
          const distance = plotData.map((dataObject: any) => dataObject.distance)
          const sohAbove = plotData.map((dataObject: any) => dataObject.sohAbove)
          const sohBelow = plotData.map((dataObject: any) => dataObject.sohBelow)
          
          //trace 1: bosch reference fleet -> status: Deleted
          
          const trace2 = {
            x: this.getConvertedMileageArray(distance),
            y: sohAbove,
            mode: 'lines',
            line: { shape: 'linear', color: '#006C3A' },
            name: 'Above average battery health'
          };

          const trace3 = {
            x: this.getConvertedMileageArray(distance),
            y: sohBelow,
            mode: 'lines',
            line: { shape: 'linear', color: '#00629A' },
            name: 'Average battery health'
          };

          const traces: { x: number[]; y: number[]; mode: string; marker: { color: string; size: number; }; name: string; }[] = [];
          const vehicleNameMap: { [key: string]: string } = {};

          this.selectedVehicles.forEach(vehicle => {
            vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
          });

          this.initializeColorMap();

          if (Object.keys(this.vehiclesInfo).length !== 0) {
            Object.keys(this.vehiclesInfo).forEach((key, index) => {
              const vehicle = this.vehiclesInfo[key];
              const mileage = vehicle.mileage
              const trace = {
                x: [this.getConvertedMileageValue(mileage)],
                y: [vehicle.batteryStateOfHealth],
                mode: 'markers',
                marker: {
                  color: this.colorMap[key],
                  size: 20
                },
                name: vehicleNameMap[key]
              };
              traces.push(trace);
            });
            this.dataLoading = false;
          } else if (this.dataLoadingError) {
            this.errorMessage = true;
            this.dataLoading = false;
          }
          
          
          const allYValues = [
            ...trace2.y,
            ...trace3.y,
            ...traces.flatMap((t: any) => t.y)
          ];
          const maxY = Math.max(...allYValues);
          this.yValues = allYValues;

          const areaAbove = {
            x: [...trace2.x, ...trace2.x.slice().reverse()],
            y: [
              ...trace2.y.map(() => maxY + 10),
              ...trace2.y.slice().reverse()
            ],
            fill: 'toself',
            fillcolor: '#00884A20',
            line: { color: 'transparent' },
            showlegend: true,
            name: 'Above Average Condition',
            hoverinfo: 'skip'
          };

          const areaBetween = {
            x: [...trace2.x, ...trace3.x.slice().reverse()],
            y: [
              ...trace2.y,
              ...trace3.y.slice().reverse()
            ],
            fill: 'toself',
            fillcolor: '#007BC020',
            line: { color: 'transparent' },
            showlegend: true,
            name: 'Average Condition',
            hoverinfo: 'skip'
          };

          const areaBelow = {
            x: [...trace3.x, ...trace3.x.slice().reverse()],
            y: [
              ...trace3.y,
              ...trace3.y.map(() => 0)
            ],
            fill: 'toself',
            fillcolor: '#ED000720',
            line: { color: 'transparent' },
            showlegend: true,
            name: 'Below Average Condition',
            hoverinfo: 'skip'
          };

          const data = [areaAbove, areaBetween, areaBelow, trace2, trace3, ...traces];

          let yRange = this.rangeYPipe.transform(this.yValues, 5);
          const layout = {
            title: 'EV Battery Condition Compared to Bosch Reference Fleet',
            font: {
              family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
              size: 13,
            },
            xaxis: {
              title: this.getMileageAxisLabel(),
            },
            yaxis: {
              title: 'State of Health [%]',
              range: yRange,
            },
            legend: {
              orientation: 'h',
              y: -0.3,
              xanchor: 'center',
              x: 0.5,
              font: {
                family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
                size: 13,
              },
              itemclick: false,
              itemdoubleclick: false,
            }
          };
          createPlotlyChart('ev-condition-plot', data, layout);
        },
        error: (error) => {
          console.error('Error fetching Reference Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  getConvertedMileageArray(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kmToMilesPipe.transform(val))
        : arr;
    }
  }

  getConvertedMileageValue(val: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? val : this.kmToMilesPipe.transform(val);
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? this.kmToMilesPipe.transform(val)
        : val;
    }
  }

  getMileageAxisLabel(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'Mileage [km]' : 'Mileage [mi]';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'Mileage [mi]'
        : 'Mileage [km]';
    }
  }

}
